// Pure trade-record helpers. No Firestore, no React.
// Used by TradePanel for shaping proposals and by CampaignContext.acceptTrade
// to compute the inventory/currency mutations on both sides.

import { CURRENCY_DENOMS, createItem } from './inventory';

const EMPTY_OFFER = () => ({
  items: [],
  currency: { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 },
});

// Snapshot just the fields we need to render + reapply on accept.
function snapshotItem(item, qty) {
  return {
    id: item.id,
    name: item.name,
    qty: Math.min(qty, item.qty || 0),
    description: item.description || '',
    equipped: !!item.equipped,
    category: item.category,
  };
}

export function createTradeProposal({ from, to, fromOffer, toOffer }) {
  return {
    fromPlayerId: from.id,
    toPlayerId:   to.id,
    fromName:     from.charName || 'Player',
    toName:       to.charName || 'Player',
    fromOffer:    fromOffer || EMPTY_OFFER(),
    toOffer:      toOffer   || EMPTY_OFFER(),
    status:       'pending',
    failedReason: null,
    createdAt:    Date.now(),
    resolvedAt:   null,
  };
}

// Validate that `player` still owns everything in `offer`.
// Returns { ok, reason }.
export function validateOffer(player, offer) {
  if (!offer) return { ok: true };
  const inv = player?.inventory || [];
  for (const slot of offer.items || []) {
    const owned = inv.find((it) => it.id === slot.id);
    if (!owned || (owned.qty || 0) < (slot.qty || 0)) {
      return { ok: false, reason: `${player?.charName || 'Player'} no longer has ${slot.name} ×${slot.qty}` };
    }
  }
  const currency = player?.currency || {};
  for (const d of CURRENCY_DENOMS) {
    const need = (offer.currency || {})[d.id] || 0;
    if (need > 0 && (currency[d.id] || 0) < need) {
      return { ok: false, reason: `${player?.charName || 'Player'} no longer has ${need} ${d.label}` };
    }
  }
  return { ok: true };
}

// Subtract `offer` from `player`'s side. Returns the next {inventory, currency}.
export function subtractOffer(player, offer) {
  const inventory = (player.inventory || []).map((it) => {
    const slot = offer?.items?.find((s) => s.id === it.id);
    if (!slot) return it;
    const remaining = (it.qty || 0) - (slot.qty || 0);
    return { ...it, qty: Math.max(0, remaining) };
  }).filter((it) => (it.qty || 0) > 0); // drop zero-qty rows

  const currency = { ...(player.currency || {}) };
  for (const d of CURRENCY_DENOMS) {
    const taken = (offer?.currency || {})[d.id] || 0;
    currency[d.id] = Math.max(0, (currency[d.id] || 0) - taken);
  }
  return { inventory, currency };
}

// Add `offer` to `player`'s side. Stacks by name when an entry already exists,
// otherwise creates a new item with a fresh id (carrying snapshot fields).
export function addOffer(player, offer) {
  let inventory = [...(player.inventory || [])];
  for (const slot of offer?.items || []) {
    const existing = inventory.findIndex((it) => it.name === slot.name && it.category === slot.category);
    if (existing >= 0) {
      const it = inventory[existing];
      inventory[existing] = { ...it, qty: (it.qty || 0) + (slot.qty || 0) };
    } else {
      // Fresh item — new id, recipient owns it cleanly. Equipped resets to false
      // (pickup defaults to unequipped; recipient can equip themselves).
      inventory.push(createItem(slot.category, {
        name: slot.name,
        qty: slot.qty,
        description: slot.description || '',
        equipped: false,
      }));
    }
  }
  const currency = { ...(player.currency || {}) };
  for (const d of CURRENCY_DENOMS) {
    const got = (offer?.currency || {})[d.id] || 0;
    currency[d.id] = (currency[d.id] || 0) + got;
  }
  return { inventory, currency };
}

// Compose subtract + add for one player given their two offers.
// `mine` = the offer this player is giving up, `theirs` = what they receive.
export function applyTradeToPlayer(player, mine, theirs) {
  const afterSubtract = subtractOffer(player, mine);
  const next          = addOffer({ ...player, ...afterSubtract }, theirs);
  return next; // {inventory, currency}
}

export function offerIsEmpty(offer) {
  if (!offer) return true;
  if ((offer.items || []).length > 0) return false;
  for (const d of CURRENCY_DENOMS) {
    if ((offer.currency || {})[d.id] > 0) return false;
  }
  return true;
}
